import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Text } from '@mantine/core';
import { upperFirst } from '@mantine/hooks';
import NoNds from '@/components/common/NoNds';
import type { AttentionItem, AttentionView } from '@/lib/dashboard';
import { formatMoney, formatOrgName, formatWeightTon } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import classes from './home.module.css';

/** Что сказать справа под суммой: действие, текст приостановки или чего ждём. */
function rightLine(item: AttentionItem, t: (key: string) => string): { text: string; tone: 'action' | 'danger' | 'soft' } {
  if (item.kind === 'blocked') {
    return { text: item.title || upperFirst(t('app.home.action.blocked')), tone: 'danger' };
  }
  if (item.group === 'mine' && item.actionLabelKey) {
    return { text: `${upperFirst(t(item.actionLabelKey))} →`, tone: 'action' };
  }
  if (item.waitLabelKey) {
    return { text: t(item.waitLabelKey), tone: 'soft' };
  }
  // Шаг автоматический или неизвестный: прежние тексты «Новая сделка», «Срок сделки истёк».
  return { text: upperFirst(t(item.actionLabelKey ?? 'app.home.action.overdue')), tone: 'soft' };
}

function AttentionRow({ item }: { readonly item: AttentionItem }) {
  const { t } = useTranslation();
  const counterparty = formatOrgName(item.counterparty);
  // Первая строка — что за сделка: «Пшеница 5 класс, 1 000 т · ООО «АГРОСБЫТ»». Без товара — номер сделки.
  const goods = [item.goods, item.weightTon != null ? formatWeightTon(item.weightTon) : undefined].filter(Boolean).join(', ');
  const headline = [goods || t('app.home.deal', { no: item.dealNo }), counterparty].filter(Boolean).join(' · ');
  const right = rightLine(item, t);
  const rightColor = right.tone === 'action' ? 'brand.7' : right.tone === 'danger' ? 'loss.7' : 'gray.7';

  return (
    <Link to={`${ROUTES.APP_DEALS}/${item.dealId}`} className={`${classes.row} ${classes.item}`}>
      <Text fw={600} size="md">
        {headline}
      </Text>
      <Text fw={600} fz={18} className={classes.itemRight} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {item.amount != null ? (
          <>
            {formatMoney(item.amount)} ₽
            <Text span size="sm">
              <NoNds />
            </Text>
          </>
        ) : null}
      </Text>
      <Text size="sm" c="gray.7">
        {t('app.home.dealShort', { no: item.dealNo })}
        {item.delayDays ? (
          <>
            {' · '}
            <span className={classes.late}>{t('app.home.attention.lateDays', { count: item.delayDays })}</span>
          </>
        ) : null}
      </Text>
      <Text size="sm" fw={right.tone === 'soft' ? 400 : 600} c={rightColor} className={classes.itemRight}>
        {right.text}
      </Text>
    </Link>
  );
}

function AttentionGroupRows({ title, items }: { readonly title: string; readonly items: readonly AttentionItem[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <>
      <h3 className={classes.groupTitle}>{title}</h3>
      {items.map((item) => (
        <AttentionRow key={item.key} item={item} />
      ))}
    </>
  );
}

// Лента дел двумя группами: «Ваш ход» и «Ждём других». Каждая строка — ссылка на карточку сделки.
export default function AttentionList({ view }: { readonly view: AttentionView }) {
  const { t } = useTranslation();

  return (
    <div>
      <AttentionGroupRows title={t('app.home.attention.mine')} items={view.mine} />
      <AttentionGroupRows title={t('app.home.attention.waiting')} items={view.waiting} />
      {view.hidden > 0 ? (
        <Link to={ROUTES.APP_DEALS} className={classes.row}>
          <Text size="sm" c="brand.7" fw={600}>
            {t('app.home.attention.more', { count: view.hidden })}
          </Text>
        </Link>
      ) : null}
    </div>
  );
}
